import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import IPSelectBox from '../../../../../components/IPSelectBox'
import Table from '../../../../../components/Table'
import { DEFAULT_EXCLUDE_TEST_DEALERS } from '../analyticsQuery'
import { exportUsageToExcel } from '../dealerUsageExcel'
import { DealerTypeFilter } from '../analyticsQuery'
import { IChatQuestionReport, IQuestionReportParams, QuestionSource, QuestionVoteFilter } from '../models'
import { buildQuestionSheetRows } from '../reportExcel'
import {
  formatLocalDateTime,
  isButtonQuestion,
  questionSourceLabel,
  voteIcon,
  DEFAULT_LOAD_ERROR_TEXT,
  loadErrorMessage,
} from '../viewModel'
import { UsagePagination } from './DealerUsageTab'
import QuestionAnswerCell from './QuestionAnswerCell'
import { RemoteBadge } from './RemoteBadge'
import styles from './ChatbotAnalytics.module.scss'

const PAGE_SIZE = 50
const EXPORT_PAGE_SIZE = 5000
const SEARCH_DEBOUNCE_MS = 400
const DASH = '—'

type LoadState = 'loading' | 'ready' | 'empty' | 'error'

export type QuestionReportLoader = (params: IQuestionReportParams) => Promise<IChatQuestionReport>

export interface QuestionsTabProps {
  fromUtc: Date
  toUtc: Date
  loadQuestionReport: QuestionReportLoader
  /** "Deneme Kartı Hariç" (shell'den gelir; Excel dahil her isteğe eklenir). */
  excludeTestDealers?: boolean
  /** "Bayi Tipi" (shell'den gelir; Excel dahil her isteğe eklenir). */
  dealerType?: DealerTypeFilter
}

/** Sunucudaki `ChatTurnOutcomes` kümesi; ham kod gösterilir (Genel Bakış'taki sonuç kırılımıyla aynı). */
const outcomeOptions = [
  { value: '', label: 'Tüm sonuçlar' },
  ...[
    'success',
    'partialSuccess',
    'noResult',
    'clarification',
    'fallback',
    'outOfScope',
    'capabilityLimit',
    'securityRefusal',
    'error',
    'cancelled',
  ].map(outcome => ({ value: outcome, label: outcome })),
]

/** Boş = yazılan + buton hepsi (`source` gönderilmez). */
const sourceOptions: { value: '' | QuestionSource; label: string }[] = [
  { value: '', label: 'Tümü' },
  { value: 'typed', label: 'Yazılan' },
  { value: 'button', label: 'Buton' },
]

const voteOptions: { value: '' | QuestionVoteFilter; label: string }[] = [
  { value: '', label: 'Tüm oylar' },
  { value: 'up', label: '👍 Olumlu' },
  { value: 'down', label: '👎 Olumsuz' },
  { value: 'none', label: '– Oysuz' },
]

/** Bayilerin sorduğu sorular, BAYİ düzeyinde (ADR-021): kullanıcı kimliği gösterilmez. */
export default function QuestionsTab({
  fromUtc,
  toUtc,
  loadQuestionReport,
  excludeTestDealers = DEFAULT_EXCLUDE_TEST_DEALERS,
  dealerType,
}: QuestionsTabProps) {
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  const [textDraft, setTextDraft] = useState('')
  const [text, setText] = useState('')
  const [intentDraft, setIntentDraft] = useState('')
  const [intent, setIntent] = useState('')
  const [source, setSource] = useState<QuestionSource | undefined>(undefined)
  const [outcome, setOutcome] = useState('')
  const [vote, setVote] = useState<QuestionVoteFilter | undefined>(undefined)
  const [page, setPage] = useState(1)
  const [loadState, setLoadState] = useState<LoadState>('loading')
  // Sunucunun kullanıcıya dönük hata metni (ör. 90 gün sınırı) panelde gösterilir.
  const [errorMessage, setErrorMessage] = useState(DEFAULT_LOAD_ERROR_TEXT)
  const [report, setReport] = useState<IChatQuestionReport | null>(null)
  // Cevap satır genişletilince açılır (Geri Bildirimler sekmesiyle aynı davranış).
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({})
  const [exporting, setExporting] = useState(false)

  const toggleRow = (index: number) =>
    setExpandedRows(current => ({ ...current, [index]: !current[index] }))

  const requestSequenceRef = useRef(0)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      requestSequenceRef.current += 1
    }
  }, [])

  // Serbest metin kutuları her tuşta istek atmaz; terim değişince sayfa 1'e döner (DealerUsageTab deseni).
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(current => (current === searchDraft ? current : searchDraft))
      setText(current => (current === textDraft ? current : textDraft))
      setIntent(current => (current === intentDraft ? current : intentDraft))
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchDraft, textDraft, intentDraft])

  const currentParams = useCallback(
    (overrides?: Partial<IQuestionReportParams>): IQuestionReportParams => ({
      fromUtc,
      toUtc,
      source,
      search: search.trim() || undefined,
      text: text.trim() || undefined,
      intent: intent.trim() || undefined,
      outcome: outcome || undefined,
      vote,
      page,
      pageSize: PAGE_SIZE,
      export: false,
      excludeTestDealers,
      dealerType,
      ...overrides,
    }),
    [fromUtc, toUtc, source, search, text, intent, outcome, vote, page, excludeTestDealers, dealerType]
  )

  const load = useCallback(async () => {
    const sequence = ++requestSequenceRef.current
    setLoadState('loading')

    try {
      const response = await loadQuestionReport(currentParams())
      // Geç dönen yanıt yeniyi EZMEZ.
      if (!mountedRef.current || sequence !== requestSequenceRef.current) return
      setReport(response)
      setExpandedRows({})
      setLoadState(response.Page.TotalRows === 0 ? 'empty' : 'ready')
    } catch (error) {
      if (!mountedRef.current || sequence !== requestSequenceRef.current) return
      setErrorMessage(loadErrorMessage(error))
      setLoadState('error')
    }
  }, [currentParams, loadQuestionReport])

  useEffect(() => {
    load()
  }, [load])

  const exportToExcel = async () => {
    setExporting(true)
    try {
      // Sayfalanmış görünüm değil, aynı filtrelerin TAM sonuç kümesi (en fazla 5000) indirilir.
      const response = await loadQuestionReport(currentParams({ page: 1, pageSize: EXPORT_PAGE_SIZE, export: true }))
      exportUsageToExcel(buildQuestionSheetRows(response.Page.Rows), 'chatbot-sorular.xlsx', 'Sorular')
    } catch (error) {
      if (mountedRef.current) {
        setErrorMessage(loadErrorMessage(error))
        setLoadState('error')
      }
    } finally {
      if (mountedRef.current) setExporting(false)
    }
  }

  const columns = useMemo(
    () => [
      { Header: 'Tarih', accessor: 'date', disableSortBy: true },
      { Header: 'Bayi', accessor: 'dealer', disableSortBy: true },
      { Header: 'Soru', accessor: 'question', disableSortBy: true },
      { Header: 'Nasıl soruldu', accessor: 'source', disableSortBy: true },
      { Header: 'Intent', accessor: 'intent' },
      { Header: 'Sonuç', accessor: 'outcome' },
      { Header: 'Oy', accessor: 'vote', disableSortBy: true },
    ],
    []
  )

  const rows = useMemo(
    () =>
      (report?.Page.Rows ?? []).map((row, index) => {
        const button = isButtonQuestion(row)
        const expanded = !!expandedRows[index]

        return {
          date: (
            <span className={styles.expandCell}>
              <button
                type='button'
                className={styles.expandButton}
                data-testid={`questions-expand-${index}`}
                aria-expanded={expanded}
                aria-label={expanded ? 'Satırı daralt' : 'Satırı genişlet'}
                onClick={() => toggleRow(index)}
              >
                {expanded ? '▾' : '▸'}
              </button>
              {formatLocalDateTime(row.AskedAtUtc)}
            </span>
          ),
          dealer: (
            <span>
              {row.DealerName ?? DASH}
              <small className={styles.dealerCode}> {row.DealerSapCode}</small>
              {row.IsRemote ? <RemoteBadge testId={`questions-row-${index}-remote`} /> : null}
            </span>
          ),
          question: (
            <QuestionAnswerCell
              question={row.Question}
              answer={row.Answer}
              questionSource={row.QuestionSource}
              buttonIntent={row.ButtonIntent}
              showSourceBadge={false}
              expanded={expanded}
              testIdPrefix={`questions-row-${index}`}
            />
          ),
          source: (
            <span
              data-testid={`questions-row-${index}-source`}
              className={`${styles.sourceBadge} ${button ? styles.sourceBadgeButton : styles.sourceBadgeTyped}`}
            >
              {questionSourceLabel(row)}
            </span>
          ),
          intent: row.Intent ?? DASH,
          outcome: row.Outcome ?? DASH,
          vote: (
            <span data-testid={`questions-row-${index}-vote`} aria-label={row.Vote ? `Oy: ${row.Vote}` : 'Oy yok'}>
              {voteIcon(row.Vote)}
            </span>
          ),
        }
      }),
    [report, expandedRows]
  )

  return (
    <section className={styles.page} data-testid='questions-tab-panel'>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Site Raporları</span>
          <h1>Sorular</h1>
          <p>
            Seçilen aralıkta bayilerin B-Gini&apos;ye sorduğu soruları inceleyin; satırı açarak
            B-Gini&apos;nin verdiği cevabı görün.
          </p>
        </div>
        <span className={styles.privacyBadge}>Bayi düzeyinde gösterilir</span>
      </header>

      <div className={styles.usageToolbar}>
        <input
          type='text'
          data-testid='questions-search'
          className={styles.dateInput}
          value={searchDraft}
          aria-label='Bayi araması'
          placeholder='Bayi ünvanı veya SAP kodu'
          onChange={event => setSearchDraft(event.target.value)}
        />
        <input
          type='text'
          data-testid='questions-text'
          className={styles.dateInput}
          value={textDraft}
          aria-label='Metin araması'
          placeholder='Soru metninde ara'
          onChange={event => setTextDraft(event.target.value)}
        />
        <input
          type='text'
          data-testid='questions-intent'
          className={styles.dateInput}
          value={intentDraft}
          aria-label='Intent filtresi'
          placeholder='Intent (tam eşleşme)'
          onChange={event => setIntentDraft(event.target.value)}
        />
        <div className={styles.toolbarSelect}>
          <IPSelectBox
            id='questions-source-filter'
            aria-label='Soru tipi'
            options={sourceOptions}
            value={sourceOptions.find(option => option.value === (source ?? '')) ?? sourceOptions[0]}
            onChangeSingle={option => {
              setSource(option?.value ? (String(option.value) as QuestionSource) : undefined)
              setPage(1)
            }}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className={styles.toolbarSelect}>
          <IPSelectBox
            id='questions-outcome-filter'
            aria-label='Sonuç filtresi'
            options={outcomeOptions}
            value={outcomeOptions.find(option => option.value === outcome) ?? outcomeOptions[0]}
            onChangeSingle={option => {
              setOutcome(option?.value ? String(option.value) : '')
              setPage(1)
            }}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className={styles.toolbarSelect}>
          <IPSelectBox
            id='questions-vote-filter'
            aria-label='Oy filtresi'
            options={voteOptions}
            value={voteOptions.find(option => option.value === (vote ?? '')) ?? voteOptions[0]}
            onChangeSingle={option => {
              setVote(option?.value ? (String(option.value) as QuestionVoteFilter) : undefined)
              setPage(1)
            }}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <button
          type='button'
          data-testid='questions-export'
          className={styles.secondaryButton}
          disabled={exporting}
          onClick={exportToExcel}
        >
          Excel&apos;e Aktar
        </button>
      </div>

      {loadState === 'error' ? (
        <section className={`${styles.statePanel} ${styles.errorState}`} role='alert' data-testid='questions-error'>
          <span className={styles.stateIcon} aria-hidden='true'>
            !
          </span>
          <h2>Sorular yüklenemedi</h2>
          <p>{errorMessage}</p>
        </section>
      ) : null}

      {loadState === 'empty' ? (
        <section className={styles.statePanel} role='status' data-testid='questions-empty'>
          <span className={styles.stateIcon} aria-hidden='true'>
            —
          </span>
          <h2>Henüz veri yok</h2>
          <p>Seçilen aralıkta ve filtrelerde soru bulunamadı.</p>
        </section>
      ) : null}

      {loadState === 'loading' ? (
        <section className={styles.statePanel} role='status' aria-live='polite' data-testid='questions-loading'>
          <span className={styles.loadingMark} aria-hidden='true' />
          <h2>Sorular yükleniyor</h2>
        </section>
      ) : null}

      {report && loadState === 'ready' ? (
        <>
          <div className={styles.reportTable}>
            <Table columns={columns} data={rows} initialPageSize={PAGE_SIZE} hidePagination />
          </div>
          <UsagePagination
            page={page}
            pageSize={PAGE_SIZE}
            totalRows={report.Page.TotalRows}
            onPageChange={setPage}
            testIdPrefix='questions'
          />
        </>
      ) : null}
    </section>
  )
}
